// Tipos do módulo Ordem de Serviço (OS) — espelham o contrato do backend
// (bd-services-producer-analytics).

export type OsStatus =
  | 'EM_ABERTO'
  | 'EM_PREENCHIMENTO'
  | 'AGUARDANDO_APROVACAO'
  | 'APROVADA'
  | 'REJEITADA'

export type OsImportStatus = 'PENDENTE' | 'PARCIAL' | 'IMPORTADA'
export type OsTipo = 'LOCAL' | 'VIAGEM'
export type OsOrigemLeitor = 'BD' | 'OLC' | 'ROSEMBERG'
export type OsEquipamentoTipo =
  | 'LEITOR'
  | 'SMART'
  | 'NETBOOK'
  | 'NOTEBOOK'
  | 'TEF'
  | 'EPSON'
  | 'ZEBRA'
  | 'CELULAR'
  | 'CARREGADOR'

// Permissões (cadastradas no painel)
export const OS_PERM = {
  VISUALIZAR: 'OS1',
  LANCAR: 'OS2',
  APROVAR: 'OS3',
  IMPORTAR: 'OS4',
  CONFIGURAR: 'OS5',
  REVERTER: 'OS6',
  IMPORTAR_PARCIAL: 'OS7',
  GERENCIAR_TECNICOS: 'OS8',
  ANALYTICS: 'OS9',
} as const

export const STATUS_LABEL: Record<OsStatus, string> = {
  EM_ABERTO: 'Em Aberto',
  EM_PREENCHIMENTO: 'Em Preenchimento',
  AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
  APROVADA: 'Aprovada',
  REJEITADA: 'Rejeitada',
}

// ---- Listagem ----
export interface OsListRow {
  id: number
  numero: number
  eventoId: number
  pracaId: number
  praca: string | null
  clienteId: number
  produtor: string | null
  evento: string | null
  dataTermino: string | null
  responsavel: string | null
  temAtendimento: number | null
  status: OsStatus
  importStatus: OsImportStatus
  custoTotal: string
  fechamentoStatus: string | null
  // Posição do evento entre os eventos do produtor (por data) / total do produtor
  produtorEventoRank: number | null
  produtorTotalEventos: number | null
}

export interface OsKpis {
  total: number
  pendentes: number
  aguardando: number
  aprovadasAImportar: number
  importadas: number
}

export interface OsCharts {
  atendimento: { com: number; sem: number; aDefinir: number }
  tipo: { local: number; viagem: number }
}

export interface OsPracaOption {
  id: number
  nome: string
}

export interface OsListResponse {
  success: boolean
  data: OsListRow[]
  kpis: OsKpis
  charts: OsCharts
  page: number
  last_page: boolean
  pracas?: OsPracaOption[]
}

export interface OsListFilters {
  praca?: string
  status?: string
  atendimento?: string // '0' | '1'
  tipo?: string // LOCAL | VIAGEM
  produtor?: string
  responsavel?: string
  evento?: string
  dataIni?: string
  dataFim?: string
  page?: number
  sortBy?: string
  sortDir?: string // asc | desc
}

// ---- Analytics de Operações (OS9) ----
export interface OsAnalyticsFilters {
  praca?: string
  status?: string
  atendimento?: string
  tipo?: string
  produtor?: string
  responsavel?: string
  evento?: string
  importStatus?: string
  origem?: string
  dataIni?: string
  dataFim?: string
  dateField?: 'evento' | 'criacao' | 'aprovacao' | 'importacao'
}

export interface OsAnalyticsCategorias {
  tecnicos: number
  staff: number
  alimentacao: number
  deslocamento: number
  hospedagem: number
  equipamentos: number
  logistica: number
}

export interface OsAnalyticsRankingItem {
  id: number | null
  nome: string
  qtdOs: number
  custoTotal: number
  custoMedio: number
  tecnicos: number
  equipamentos: number
  retornoQtd: number
  retornoValor: number
}

export interface OsAnalyticsEvento {
  osId: number
  numero: number
  eventoId: number | null
  evento: string | null
  produtor: string | null
  praca: string | null
  pracaId: number | null
  dataEvento: string | null
  status: OsStatus
  importStatus: OsImportStatus
  temAtendimento: number | null
  tipo: OsTipo | null
  qtdTecnicos: number
  emitidos: number
  equipamentosQtd: number
  categorias: OsAnalyticsCategorias
  custoOperacional: number
  custoTotal: number
}

export interface OsAnalyticsRetornoOs {
  osId: number
  numero: number
  praca: string | null
  evento: string | null
  produtor: string | null
  dataEvento: string | null
  tipos: Partial<Record<OsEquipamentoTipo, { qtd: number; valor: number }>>
  totalQtd: number
  totalValor: number
}

export interface OsAnalyticsTecnico {
  tecnicoId: number
  nome: string
  eventos: number
  eventosViagem: number
  eventosLocais: number
  eventosADefinir: number
  turnos: number
  horas: number
  cacheTotal: number
  alimentacao: number
  cacheMedioPorEvento: number
  valorHora: number | null
}

export interface OsAnalytics {
  kpis: {
    totalOs: number
    produtores: number
    pracas: number
    emitidos: number
    tecnicosAlocacoes: number
    tecnicosDistintos: number
    leitoresUtilizados: number
    maquinas: number
    equipamentosRetorno: number
    equipamentosTotal: number
    custoOperacional: number
    custoTotal: number
    custoMedioPorOs: number
    custoPorIngresso: number | null
    osSemCusto: number
  }
  contadores: {
    atendimento: { com: number; sem: number; aDefinir: number }
    tipo: { local: number; viagem: number; aDefinir: number }
    status: {
      emAberto: number
      emPreenchimento: number
      aguardando: number
      aprovadas: number
      rejeitadas: number
    }
    importacao: { pendentes: number; parciais: number; importadas: number; aprovadasAImportar: number }
  }
  custos: { categorias: OsAnalyticsCategorias; operacional: number; total: number }
  evolucaoMensal: Array<{ mes: string; qtdOs: number; custoTotal: number; custoOperacional: number }>
  equipe: {
    alocacoes: number
    tecnicosDistintos: number
    valorTotal: number
    valorMedioPorAlocacao: number
    mediaPorEvento: number
    staffValor: number
    alimentacaoValor: number
  }
  leitores: {
    sugeridos: number
    utilizados: number
    diferenca: number
    diferencaPct: number | null
    mediaPorEvento: number
    ingressosPorLeitor: number | null
    porOrigem: Array<{ origem: string; leitores: number }>
  }
  deslocamento: {
    kmTotal: number
    combustivel: number
    pedagio: number
    uber: number
    estacionamento: number
    aluguelCarro: number
    hospedagem: number
    alimentacao: number
    total: number
    viagemTotal: number
    eventosViagem: number
  }
  logistica: { total: number; linhas: number; osComCusto: number; mediaPorOs: number }
  retorno: {
    totalQtd: number
    totalValor: number
    valorMedioPorEquipamento: number
    osComRetorno: number
    mediaPorOs: number
    porTipo: Array<{ tipo: OsEquipamentoTipo; qtd: number; valor: number }>
    porOs: OsAnalyticsRetornoOs[]
  }
  rankings: {
    pracas: OsAnalyticsRankingItem[]
    eventos: OsAnalyticsRankingItem[]
    produtores: OsAnalyticsRankingItem[]
  }
  importacaoP2: {
    importadoProdutor: number
    importadoEmpresa: number
    pendenteImportacao: number
    tempoMedioAprovacaoDias: number | null
    tempoMedioImportacaoDias: number | null
    importadasPorMes: Array<{ mes: string; qtdOs: number }>
  }
  tecnicos: OsAnalyticsTecnico[]
  eventos: OsAnalyticsEvento[]
}

// ---- Detalhe / blocos ----
export interface OsTurno {
  id?: number
  data: string
  chegada: string
  saida: string
  minutosTrabalhados?: number
  cacheCalculado?: string
}

export interface OsEquipeItem {
  id?: number
  tecnicoId: number
  cacheCalculado?: string
  alimentacao?: string
  alimDiasViagem?: number
  emailSnapshot?: string | null
  telefoneSnapshot?: string | null
  turnos: OsTurno[]
}

export interface OsCustos {
  staff?: number
  kmRodado?: number
  combustivel?: number
  pedagio?: number
  aluguelCarro?: number
  uber?: number
  estacionamento?: number
  hospedagem?: number
  outros?: string | null
}

export interface OsCustoLogistica {
  id: number
  data: string
  usuarioNome: string | null
  descricao: string
  valor: string
  criadoEm: string
}

export interface OsLeitorSetor {
  id?: number
  setorId?: number | null
  setor: string
  emitidos: number
  leitoresSugeridos?: number
  leitoresUtilizados?: number
  origem?: OsOrigemLeitor | null
}

export interface OsMaquina {
  id?: number
  tipo: string
  serial?: string | null
}

export interface OsEquipamento {
  id?: number
  tipo: OsEquipamentoTipo
  qtd: number
  valorUnit: number | string
}

export interface OsAnexo {
  id: number
  nomeDespesa: string
  arquivoUrl: string
  arquivoUrlView?: string
  tipo: string | null
  tamanho: number | null
}

export interface OsLog {
  id: number
  usuarioId: number | null
  usuarioNome: string | null
  acao: string
  criadoEm: string
}

export interface OsHeader {
  id: number
  numero: number
  eventoId: number
  pracaId: number
  clienteId: number
  responsavelId: number | null
  responsavelNome: string | null
  status: OsStatus
  temAtendimento: number | null
  tipo: OsTipo | null
  motivoRejeicao: string | null
  obsAtendimento: string | null
  importStatus: OsImportStatus
  custoTotal: string
  aprovadoEm: string | null
  dataHoraCriacao: string
}

export interface OsDetail {
  os: OsHeader
  evento: {
    evento: string
    dataTermino: string | null
    local: string | null
    eventoCancelado: number
    produtor: string
    praca: string
    fechamentoStatus: string | null
  } | null
  totalEmitidos: number
  custos: OsCustos | null
  equipe: OsEquipeItem[]
  leitorSetores: OsLeitorSetor[]
  maquinas: OsMaquina[]
  equipamentos: OsEquipamento[]
  custoLogistica: OsCustoLogistica[]
  anexos: OsAnexo[]
  logs: OsLog[]
}

// ---- Técnico ----
export interface OsTecnicoDropdown {
  id: number
  nome: string
  email: string | null
  telefone: string | null
}

export interface OsTecnico extends OsTecnicoDropdown {
  cpf: string
  rg: string | null
  nascimento: string | null
  cep: string | null
  logradouro: string | null
  numero: string | null
  bairro: string | null
  cidade: string | null
  uf: string | null
  banco: string | null
  agencia: string | null
  conta: string | null
  contaTipo: 'CORRENTE' | 'POUPANCA' | null
  documentoUrl: string | null
  ativo: number
}

// ---- Config ----
export interface OsConfig {
  id: number
  pisoCache: string
  horasPiso: number
  valorHoraExtra: string
  toleranciaMinutos: number
  kmValor: string
  alimLocal: string
  alimLocalMinExtras: number
  alimViagemDia: string
  divisorLeitor: number
  leitorOlcEmpresa: string
  leitorRosembergPct: string
  equipValorLeitor: string
  equipValorSmart: string
  equipValorNetbook: string
  equipValorNotebook: string
  equipValorTef: string
  equipValorEpson: string
  equipValorZebra: string
  equipValorCelular: string
  equipValorCarregador: string
  vigenciaInicio: string
}

// ---- Payload de save ----
export interface SaveOsPayload {
  id: number
  temAtendimento?: number | null
  tipo?: OsTipo | null
  obsAtendimento?: string | null
  custos?: OsCustos
  equipe?: Array<{ tecnicoId: number; alimDiasViagem?: number; turnos?: OsTurno[] }>
  leitorSetores?: OsLeitorSetor[]
  maquinas?: OsMaquina[]
  equipamentos?: OsEquipamento[]
}
